import { useState } from 'react';
import { useParams, Link } from '@tanstack/react-router';
import {
  Card,
  Title,
  Text,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@tremor/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Mail, PlusCircle } from 'lucide-react';

import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmModal } from '@/components/ConfirmModal';
import { TimelineList, type TimelineEvent } from '@/components/TimelineList';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

interface UserDetail {
  user: {
    id: number;
    email: string;
    name: string | null;
    role: string | null;
    org_id: number | null;
    is_active: boolean;
    created_at: string | null;
    last_login: string | null;
  };
  organization: {
    id: number;
    name: string;
    slug: string;
    credit_balance: number;
    plan_type: string;
    created_at: string | null;
  } | null;
  stats: {
    uploads: number;
    calcs_done: number;
    payments_count: number;
    payments_total: number;
  };
  timeline: TimelineEvent[];
}

interface PaginatedRows<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

interface CaseRow {
  id: number;
  filename: string | null;
  status: string | null;
  status_message: string | null;
  created_at: string | null;
  pdf_calculo_path: string | null;
  pjc_calculo_path: string | null;
  org_id: number | null;
}

interface PaymentRow {
  id: number;
  type: string;
  amount: number;
  balance_after: number;
  description: string | null;
  package_key: string | null;
  created_at: string | null;
}

interface ActivityRow {
  id: number;
  event_type: string;
  event_name: string | null;
  page_url: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  ip_address: string | null;
  created_at: string | null;
}

function initials(name: string | null, email: string): string {
  const base = name?.trim() || email;
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR');
  } catch {
    return iso;
  }
}

type ModalKind = null | 'suspend' | 'unsuspend' | 'grant' | 'magic';

export function UserDetailPage() {
  const params = useParams({ strict: false }) as { userId?: string };
  const userId = Number(params.userId);
  const qc = useQueryClient();

  const detailQuery = useQuery<UserDetail>({
    queryKey: ['user.detail', userId],
    queryFn: async () => (await api().get(`/users/${userId}`)).data,
    enabled: Number.isFinite(userId),
  });

  const casesQuery = useQuery<PaginatedRows<CaseRow>>({
    queryKey: ['user.cases', userId],
    queryFn: async () => (await api().get(`/users/${userId}/cases?per_page=50`)).data,
    enabled: Number.isFinite(userId),
  });

  const paymentsQuery = useQuery<PaginatedRows<PaymentRow>>({
    queryKey: ['user.payments', userId],
    queryFn: async () => (await api().get(`/users/${userId}/payments?per_page=50`)).data,
    enabled: Number.isFinite(userId),
  });

  const activityQuery = useQuery<PaginatedRows<ActivityRow>>({
    queryKey: ['user.activity', userId],
    queryFn: async () => (await api().get(`/users/${userId}/activity?per_page=50`)).data,
    enabled: Number.isFinite(userId),
  });

  const [modal, setModal] = useState<ModalKind>(null);
  const [grantAmount, setGrantAmount] = useState<number>(10);
  const [actionError, setActionError] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);

  const suspendMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) =>
      (await api().post(`/users/${userId}/suspend`, { reason })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user.detail', userId] });
      setModal(null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao suspender o usuário.')),
  });

  const unsuspendMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) =>
      (await api().post(`/users/${userId}/unsuspend`, { reason })).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user.detail', userId] });
      setModal(null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao reativar o usuário.')),
  });

  const grantMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) =>
      (
        await api().post(`/users/${userId}/grant-credits`, {
          amount: grantAmount,
          reason,
        })
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user.detail', userId] });
      void qc.invalidateQueries({ queryKey: ['user.payments', userId] });
      setModal(null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao conceder créditos.')),
  });

  const magicMutation = useMutation({
    mutationFn: async () =>
      (await api().post(`/users/${userId}/send-magic-link`, {})).data,
    onSuccess: (data) => {
      setMagicLink((data as { link?: string }).link ?? null);
      setActionError(null);
    },
    onError: (e) =>
      setActionError(extractErrorMessage(e, 'Falha ao gerar magic-link.')),
  });

  if (!Number.isFinite(userId)) {
    return <Text>ID inválido.</Text>;
  }

  const detail = detailQuery.data;

  return (
    <div className="space-y-6">
      <Link
        to="/users"
        className="text-xs text-slate-400 hover:text-slate-200"
      >
        ← Todos os usuários
      </Link>

      {detailQuery.isLoading && <LoadingSkeleton rows={6} />}

      {detailQuery.isError && (
        <div className="rounded border border-red-900/40 bg-red-950/30 p-3 text-sm text-red-300">
          Falha ao carregar usuário.
        </div>
      )}

      {detail && (
        <>
          <Card>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600/20 text-lg font-semibold text-blue-200">
                {initials(detail.user.name, detail.user.email)}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Title>{detail.user.name ?? detail.user.email}</Title>
                  <StatusBadge
                    status={detail.user.is_active ? 'active' : 'banned'}
                  />
                </div>
                <Text className="text-sm">{detail.user.email}</Text>
                <Text className="text-xs text-slate-500">
                  {detail.organization?.name ?? 'sem organização'} ·{' '}
                  signup {fmtDateTime(detail.user.created_at)} · last login{' '}
                  {fmtDateTime(detail.user.last_login)}
                </Text>
              </div>
              <div className="flex flex-wrap gap-2">
                {detail.user.is_active ? (
                  <button
                    type="button"
                    onClick={() => {
                      setActionError(null);
                      setModal('suspend');
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-red-600/20 px-3 py-1.5 text-sm text-red-300 hover:bg-red-600/30"
                  >
                    <Ban className="h-3.5 w-3.5" /> Suspender
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setActionError(null);
                      setModal('unsuspend');
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600/20 px-3 py-1.5 text-sm text-emerald-300 hover:bg-emerald-600/30"
                  >
                    Reativar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setModal('grant');
                  }}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-600/20 px-3 py-1.5 text-sm text-blue-300 hover:bg-blue-600/30"
                >
                  <PlusCircle className="h-3.5 w-3.5" /> Conceder créditos
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActionError(null);
                    setMagicLink(null);
                    setModal('magic');
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                >
                  <Mail className="h-3.5 w-3.5" /> Magic link
                </button>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <TabGroup>
                <TabList>
                  <Tab>Cases</Tab>
                  <Tab>Pagamentos</Tab>
                  <Tab>Atividade</Tab>
                  <Tab>Timeline</Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <UserCasesTab loading={casesQuery.isLoading} rows={casesQuery.data?.items ?? []} />
                  </TabPanel>
                  <TabPanel>
                    <PaymentsTab loading={paymentsQuery.isLoading} rows={paymentsQuery.data?.items ?? []} />
                  </TabPanel>
                  <TabPanel>
                    <ActivityTab loading={activityQuery.isLoading} rows={activityQuery.data?.items ?? []} />
                  </TabPanel>
                  <TabPanel>
                    <Card>
                      <TimelineList
                        events={detail.timeline}
                        loading={false}
                        empty="Sem eventos no histórico."
                      />
                    </Card>
                  </TabPanel>
                </TabPanels>
              </TabGroup>
            </div>

            <Card className="lg:col-span-4">
              <Title>Metadata</Title>
              <dl className="mt-3 space-y-2 text-sm">
                <Row label="ID" value={String(detail.user.id)} />
                <Row label="Role" value={detail.user.role ?? '—'} />
                <Row
                  label="Org"
                  value={
                    detail.organization
                      ? `${detail.organization.name} (${detail.organization.plan_type})`
                      : '—'
                  }
                />
                <Row
                  label="Saldo de créditos"
                  value={
                    detail.organization
                      ? String(detail.organization.credit_balance)
                      : '—'
                  }
                />
                <Row
                  label="Uploads"
                  value={String(detail.stats?.uploads ?? 0)}
                />
                <Row
                  label="Calcs prontos"
                  value={String(detail.stats?.calcs_done ?? 0)}
                />
                <Row
                  label="Pagamentos"
                  value={String(detail.stats?.payments_count ?? 0)}
                />
                <Row
                  label="Total recebido (créditos)"
                  value={String(detail.stats?.payments_total ?? 0)}
                />
                <Row label="Last login" value={fmtDateTime(detail.user.last_login)} />
              </dl>
              <Text className="mt-4 text-xs text-slate-500">
                IP / user agent / geo virão na Phase 4 (security).
              </Text>
            </Card>
          </div>
        </>
      )}

      <ConfirmModal
        open={modal === 'suspend'}
        title="Suspender usuário"
        description="O usuário não poderá mais acessar a plataforma. A ação fica registrada no audit log."
        destructive
        requireReason
        confirmLabel="Suspender"
        busy={suspendMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={(reason) => suspendMutation.mutate({ reason })}
      />

      <ConfirmModal
        open={modal === 'unsuspend'}
        title="Reativar usuário"
        description="Restaura acesso. Audit log registrará a ação."
        requireReason
        confirmLabel="Reativar"
        busy={unsuspendMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={(reason) => unsuspendMutation.mutate({ reason })}
      />

      <ConfirmModal
        open={modal === 'grant'}
        title="Conceder créditos"
        description="Adiciona créditos ao saldo da organização do usuário."
        requireReason
        confirmLabel="Conceder"
        busy={grantMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={(reason) => grantMutation.mutate({ reason })}
      >
        <label className="block text-sm">
          <span className="mb-1 block text-slate-300">Quantidade</span>
          <input
            type="number"
            min={1}
            max={1000}
            value={grantAmount}
            onChange={(e) => setGrantAmount(Math.max(1, Number(e.target.value) || 1))}
            className="block w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100 focus:border-blue-500 focus:outline-none"
          />
        </label>
      </ConfirmModal>

      <ConfirmModal
        open={modal === 'magic'}
        title="Gerar magic link"
        description="Phase 4 fará o envio por email. Hoje o link é exibido para você copiar manualmente."
        confirmLabel={magicLink ? 'Fechar' : 'Gerar link'}
        busy={magicMutation.isPending}
        errorMessage={actionError}
        onCancel={() => setModal(null)}
        onConfirm={() => {
          if (magicLink) {
            setModal(null);
            setMagicLink(null);
          } else {
            magicMutation.mutate();
          }
        }}
      >
        {magicLink && (
          <div className="rounded border border-blue-900/40 bg-blue-950/20 p-3 text-xs">
            <p className="mb-2 text-blue-200">Link gerado:</p>
            <code className="block break-all text-blue-100">{magicLink}</code>
          </div>
        )}
      </ConfirmModal>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-slate-200">{value}</dd>
    </div>
  );
}

function UserCasesTab({ loading, rows }: { loading: boolean; rows: CaseRow[] }) {
  if (loading) return <LoadingSkeleton rows={4} />;
  if (rows.length === 0) {
    return <EmptyTab label="Este usuário ainda não tem cases." />;
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="min-w-full text-sm text-slate-200">
        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">ID</th>
            <th className="px-3 py-2 text-left">Filename</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Mensagem</th>
            <th className="px-3 py-2 text-left">Criado</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-800">
              <td className="px-3 py-2">
                <Link to={`/cases/${r.id}` as never} className="text-blue-300 hover:underline">
                  {r.id}
                </Link>
              </td>
              <td className="px-3 py-2">{r.filename ?? '—'}</td>
              <td className="px-3 py-2">
                <StatusBadge status={(r.status ?? '').toLowerCase()} />
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {r.status_message ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {fmtDateTime(r.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function PaymentsTab({ loading, rows }: { loading: boolean; rows: PaymentRow[] }) {
  if (loading) return <LoadingSkeleton rows={4} />;
  if (rows.length === 0) {
    return <EmptyTab label="Sem pagamentos para a organização." />;
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="min-w-full text-sm text-slate-200">
        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Tipo</th>
            <th className="px-3 py-2 text-right">Quantidade</th>
            <th className="px-3 py-2 text-right">Saldo</th>
            <th className="px-3 py-2 text-left">Descrição</th>
            <th className="px-3 py-2 text-left">Data</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-800">
              <td className="px-3 py-2">
                <StatusBadge status={r.type === 'purchase' ? 'done' : r.type === 'refund' ? 'failed' : 'pending'}>
                  {r.type}
                </StatusBadge>
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {r.amount > 0 ? '+' : ''}
                {r.amount}
              </td>
              <td className="px-3 py-2 text-right font-mono">{r.balance_after}</td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {r.description ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {fmtDateTime(r.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function ActivityTab({ loading, rows }: { loading: boolean; rows: ActivityRow[] }) {
  if (loading) return <LoadingSkeleton rows={4} />;
  if (rows.length === 0) {
    return <EmptyTab label="Sem eventos de analytics para este usuário." />;
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="min-w-full text-sm text-slate-200">
        <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
          <tr>
            <th className="px-3 py-2 text-left">Evento</th>
            <th className="px-3 py-2 text-left">Detalhe</th>
            <th className="px-3 py-2 text-left">Device</th>
            <th className="px-3 py-2 text-left">IP</th>
            <th className="px-3 py-2 text-left">Quando</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-800">
              <td className="px-3 py-2">{r.event_type}</td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {r.event_name ?? r.page_url ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {[r.device_type, r.browser, r.os].filter(Boolean).join(' · ') || '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {r.ip_address ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-slate-400">
                {fmtDateTime(r.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function EmptyTab({ label }: { label: string }) {
  return (
    <div className="rounded border border-dashed border-slate-700 bg-slate-900/20 p-6 text-center text-sm text-slate-400">
      {label}
    </div>
  );
}
